/**
 * O changelog que o usuário lê, em Configurações > Histórico de Versões.
 *
 * Cada entrada tem version, titulo, date (a data em que a mudança entrou de
 * verdade) e changes, com type 'feature' | 'improvement' | 'fix'.
 *
 * O `titulo` é o que a pessoa lê primeiro: em uma linha, o que aquela leva
 * entregou. O número da versão fica em segundo plano — ele serve para amarrar
 * o vídeo e o aviso, não para explicar nada a quem abre a tela. "v1.45.0" não
 * diz a um contador se vale a pena ler o que vem abaixo; "Conferência sob
 * controle e conciliação que acha a cobrança certa" diz.
 *
 * CRITÉRIO DE ENTRADA: só o que o usuário percebe usando o sistema.
 *
 * Ficam de fora as mudanças de bastidor — build, cache, fila, ferramenta de
 * release, a própria tela de changelog. Um contador que abre esta tela quer
 * saber o que mudou no trabalho dele; cada linha de infraestrutura afoga o que
 * interessa. O registro dessas mudanças é o histórico do git.
 *
 * Mudança relevante ganha vídeo, amarrado à versão pelo campo `versao` do
 * manifesto em videos.contlabs.com.br/novidades/manifesto.json.
 *
 * QUANDO NASCE UMA VERSÃO: só quando o Redhmam pede o vídeo. Não é a cada
 * mudança, nem a cada deploy.
 *
 * Versão aqui não é marco técnico, é marco de comunicação: o número serve para
 * apontar o vídeo daquela leva e para o aviso no sininho dizer "veja o que
 * mudou". Cortar versão a cada ajuste produz uma lista onde toda entrada tem um
 * ou dois itens, nenhuma tem vídeo, e o número no rodapé muda sem que nada
 * tenha sido anunciado a ninguém.
 *
 * Entre um vídeo e outro, o que o usuário percebe vai para PENDENTES, logo
 * abaixo. Quando o vídeo for pedido, PENDENTES vira o `changes` da versão nova
 * e volta a ficar vazio.
 */

/**
 * O que já está no ar e o usuário percebe, mas ainda não foi anunciado.
 *
 * Não aparece na tela: o Histórico de Versões mostra o que foi comunicado, e um
 * bloco "ainda não publicado" dentro do app publicado só confunde. Isto é a
 * anotação que evita ter que reconstruir a lista do git na hora do vídeo.
 */
export var PENDENTES = [];

export var CHANGELOG = [
  {
    version: 'v1.46.0',
    titulo: 'Extratos bancários e avisos de inadimplência no painel do cliente',
    date: '2026-10-13',
    changes: [
      // Extrato Bancário
      { type: 'feature', description: 'Aba Extrato Bancário no painel do cliente: os 12 meses do ano com a situação de cada um e o envio do PDF de cada banco, mês a mês' },
      { type: 'feature', description: 'Tarefas › Extratos dos clientes: todos os clientes mês a mês; abra o envio, confira o PDF e marque entregue, não entregue (com recado ao cliente) ou sem movimento, junto com a tarefa do Calendário' },
      { type: 'feature', description: 'Lembrete mensal por e-mail para quem ainda não mandou o extrato, com cobrança num segundo dia e botão que abre o painel direto no mês' },
      { type: 'improvement', description: 'A aba é ligada por cliente, na ficha do cliente (aba Serviços), marcando vários na lista de Clientes ou para todos em Configurações › Extrato Bancário' },
      // Avisos de inadimplência
      { type: 'feature', description: 'Avisos de inadimplência no painel do cliente: com uma mensalidade em aberto, aviso no topo com botão para falar com o financeiro pelo WhatsApp; com duas, o cliente confirma que leu; com três ou mais, o painel fica bloqueado até ele falar com o financeiro' },
      { type: 'feature', description: 'Configurações › Mensalidades: liga os avisos (começam desligados) e define a tolerância em dias, o WhatsApp do financeiro e o texto de cada nível, com a prévia de quem recebe cada um hoje' },
      { type: 'feature', description: 'Ficha do cliente, aba Usuários: nível do aviso, mensalidades em aberto, liberação do acesso por acordo (com ou sem prazo) e histórico de quem confirmou a leitura' },
      // Clientes
      { type: 'feature', description: 'Seleção na lista de Clientes: marque vários (ou todos da lista filtrada) e use a barra de baixo para exportar só os marcados ou ligar e desligar Malha Fiscal, Conciliação e Emissão de NF-e de uma vez' },
      { type: 'fix', description: 'Salvar a ficha do cliente não desliga mais o Extrato Bancário de quem estava com ele ligado' },
      // Notificações
      { type: 'fix', description: 'Marcar uma notificação como lida voltava a não lida ao recarregar a página; agora fica lida de verdade' },
      { type: 'improvement', description: 'Notificações passam a ter título, e as que apontam para uma tela ganham botão que leva direto até ela' },
      { type: 'improvement', description: 'Aviso de novidades mostra a capa do vídeo e abre o Histórico de Versões em um clique' },
    ],
  },
  {
    version: 'v1.45.0',
    titulo: 'Conferência sob controle e conciliação que acha a cobrança certa',
    date: '2026-09-05',
    changes: [
      // Conferência
      { type: 'feature', description: 'Switch "Exibir na Conferência" no cadastro da tarefa: agora é ele que decide se a tarefa aparece no módulo, no lugar da regra implícita do "Requer pagamento"' },
      { type: 'feature', description: 'Botão Ajuda na Conferência explicando as quatro condições de entrada e o que conta como cliente avisado' },
      { type: 'improvement', description: 'Ícone na lista do Cadastro de Tarefas marcando quais tarefas aparecem na Conferência' },
      // Serviços Extras
      { type: 'fix', description: 'Coluna do número do SOC voltou aos Serviços Extras: sumiu na troca da tabela, agora fica entre Status e Empresa e ordena por número' },
      { type: 'improvement', description: 'Serviços Extras abre em cerca de um segundo, no lugar dos mais de trinta de antes' },
      // Conciliação Bancária
      { type: 'feature', description: 'Código da cobrança nos cards da conciliação: ME nas mensalidades, SOC no que veio do societário — é ele que liga o recebimento à conta a receber' },
      { type: 'feature', description: 'Busca em todo o histórico na conciliação: para o cliente que acumula meses e paga tudo de uma vez, a cobrança antiga passa a aparecer' },
      { type: 'improvement', description: 'Sugestão automática da conciliação deixou de descartar cobrança com mais de 30 dias; a data agora desempata em vez de excluir' },
      { type: 'improvement', description: 'Busca do drawer da conciliação passa a encontrar pelo código, além do nome' },
      // Cadastro
      { type: 'fix', description: 'Cadastro interrompido retoma de onde parou, em vez de criar outro escritório com o mesmo CNPJ' },
      { type: 'fix', description: 'E-mails de boas-vindas saíram do caminho do cadastro: uma falha de envio não derruba mais o cadastro inteiro' },
      { type: 'fix', description: 'Tarefas longas voltaram a ser processadas — o worker não estava consumindo a fila delas' },
    ],
  },
  {
    version: 'v1.44.1',
    titulo: 'Comunicado do Simples Híbrido e e-mail que não se perde em silêncio',
    date: '2026-09-04',
    changes: [
      { type: 'feature', description: 'Comunicado do Simples Nacional Híbrido para os clientes, com os dois logos no topo' },
      { type: 'fix', description: 'Webhook de entrega de e-mail passou a validar assinatura: só o provedor pode marcar um e-mail como entregue ou rejeitado' },
      { type: 'fix', description: 'Envio de e-mail saiu da requisição e foi para a fila; falha de envio deixou de ser silenciosa' },
    ],
  },
  {
    version: 'v1.44.0',
    titulo: 'Ajustes internos',
    date: '2026-04-17',
    changes: [
      // Em desenvolvimento
    ],
  },
  {
    version: 'v1.43.0',
    titulo: 'Configurações redesenhadas, Contas a Receber e Extrato',
    date: '2026-04-17',
    changes: [
      // Configurações
      { type: 'feature', description: 'Configurações redesenhadas: cards com ícones por seção, drawer por URL, busca e botão + padronizados no topo' },
      { type: 'feature', description: 'Cadastro de Tarefas integrado em Configurações: menu lateral, edição e exclusão inline, adição rápida no topo' },
      { type: 'feature', description: 'Tarefas do Checklist agrupadas por departamento com drag-and-drop, badges numerados e tags coloridas' },
      { type: 'feature', description: 'Seleção em massa com barra flutuante em todas as tabelas de Configurações' },
      { type: 'feature', description: 'Virtualização nas tabelas de Configurações para melhor performance com muitos itens' },
      { type: 'feature', description: 'Barra de salvamento flutuante com contador de alterações pendentes' },
      { type: 'feature', description: 'Dados da Empresa redesenhado: layout com sidebar, seções colapsáveis, WhatsApp integrado no formulário' },
      { type: 'feature', description: 'Minha Conta redesenhada: Perfil e Senha em tabs, avisos em campos da empresa, rodapé fixo com botão salvar' },
      { type: 'feature', description: 'Cadências redesenhadas: lista de cards, script inline e drawer compartilhado' },
      { type: 'feature', description: 'Documentos Esporádicos com edição inline e toggle de tipo' },
      // Contas a Receber
      { type: 'feature', description: 'Novo módulo Contas a Receber com CRUD completo, filtro por origem (Mensalidades, Extras, Outros) e controle de ações por tipo' },
      // Extrato de Movimentações
      { type: 'feature', description: 'Nova página de Extrato de Movimentações com descrição inline, tags e filtros visuais' },
      // Conciliação Bancária
      { type: 'feature', description: 'Conciliação Bancária redesenhada: layout lado a lado com cards, skeleton de loading e linhas conectando correspondências' },
      { type: 'feature', description: 'Fluxo em duas etapas: busca + reconciliação separados, com drawer mais largo e filtros inline' },
      { type: 'feature', description: 'Confirmação de saldo como etapa inicial da conciliação, com validação de saldo inicial' },
      { type: 'feature', description: 'Detecção automática de cliente por CPF/CNPJ na descrição do extrato' },
      { type: 'feature', description: 'Filtro de cliente no drawer de busca com auto-detecção' },
      { type: 'feature', description: 'Botão Nova Receita direto para transações de crédito sem correspondência' },
      { type: 'feature', description: 'Função ignorar transações e linhas SALDO TOTAL ignoradas automaticamente' },
      { type: 'feature', description: 'MonthPicker compartilhado com presets rápidos (Mês Atual, Personalizado, Últimos N meses) padronizado em todo o app' },
      { type: 'feature', description: 'Badge de contagem de pendências de conciliação nos cards de contas bancárias' },
      // Certificados
      { type: 'feature', description: 'Wizard de renovação de certificados aprimorado com drawer de gerenciamento redesenhado' },
      { type: 'feature', description: 'Drawer de certificados com cards de ação e drawers aninhados' },
      // Societário
      { type: 'feature', description: 'Aba Financeiro do Societário redesenhada com card estilo Certificados e pré-preenchimento do nome da tarefa' },
      { type: 'feature', description: 'Card de emissão de NF no Societário sincronizado com o campo allow_nf do cliente' },
      // Cadastro de Tarefas (backend)
      { type: 'feature', description: 'Log de auditoria completo para tarefas: histórico de alterações e rastreabilidade' },
      { type: 'feature', description: 'Coluna "Última atualização" com ordenação na tabela de Cadastro de Tarefas' },
      // Permissões
      { type: 'feature', description: 'Módulo Configurações na árvore de permissões com controles granulares' },
      { type: 'feature', description: 'Editor de permissões em tela cheia com tooltips informativos e Sócio sempre travado' },
      { type: 'feature', description: 'Busca de páginas incluindo todas as sub-páginas de Configurações' },
      // Extras e Serviços
      { type: 'feature', description: 'Tabela de Extras em largura total com card de revisão financeira e correções no formulário de cobranças' },
      // Melhorias
      { type: 'improvement', description: 'TableVirtuoso no Cadastro de Tarefas para listagens grandes' },
      { type: 'improvement', description: 'Categorias de Contas a Receber separadas em chave própria de configuração' },
      { type: 'improvement', description: 'Quadro de Sócios com tabela em largura total' },
      // Correções
      { type: 'fix', description: 'Data de emissão e vencimento marcadas como obrigatórias no cadastro de certificados' },
      { type: 'fix', description: 'Filtro de período resetado para mês atual ao abrir a conciliação' },
      { type: 'fix', description: 'Cards de contas a receber em linha única com ellipsis e scroll horizontal' },
      { type: 'fix', description: 'Layouts e z-index corrigidos em drawers, modais e menus de Configurações' },
      { type: 'fix', description: 'Padronização de checks requires_payment com helper type-safe' },
    ],
  },
  {
    version: 'v1.42.0',
    titulo: 'Mensalidades e Serviços Extras com busca, filtros e seleção em massa',
    date: '2026-03-02',
    changes: [
      // Mensalidades
      { type: 'feature', description: 'Busca e DateNavigation no cabeçalho de Mensalidades' },
      { type: 'feature', description: 'SelectionToolbar flutuante, atalhos de mês e otimizações de performance em Mensalidades' },
      { type: 'feature', description: 'Modal de histórico em Mensalidades e filtro de deletados' },
      { type: 'feature', description: 'Confirmação por digitação para exclusão em massa de Mensalidades' },
      // Serviços Extras
      { type: 'feature', description: 'SegmentedControl no cabeçalho de Serviços Extras, substituindo Radio.Group' },
      { type: 'feature', description: 'Cards de resumo, DateNavigation e drawer de filtros em Serviços Extras' },
      // Leads
      { type: 'feature', description: 'Aba Societário no drawer de leads' },
      // Login
      { type: 'feature', description: 'Toggle de visibilidade de senha no formulário de login' },
      // Melhorias
      { type: 'improvement', description: 'Botão Ações em Mensalidades convertido para botão direto (sem dropdown) quando há apenas um item' },
      { type: 'improvement', description: 'Botão de Feedback com tema verde e ícone de mensagem' },
      // Correções
      { type: 'fix', description: 'Widget de Suporte inicializando corretamente e botão habilitado para todos os usuários' },
      { type: 'fix', description: 'Seleção de linha não dispara mais ao clicar no menu Ações em Mensalidades' },
      { type: 'fix', description: 'Botão de drawer de filtros restaurado ao lado do DateNavigation em Mensalidades' },
    ],
  },
  {
    version: 'v1.41.0',
    titulo: 'Despesas, Conciliação Bancária e o novo menu lateral',
    date: '2026-02-01',
    changes: [
      // Novos módulos
      { type: 'feature', description: 'Módulo de Despesas: controle completo de contas a pagar com categorias, vencimentos e status de pagamento' },
      { type: 'feature', description: 'Módulo de Conciliação Bancária: reconcilie extratos bancários com honorários, extras e despesas. Suporta importação de arquivos OFX' },
      { type: 'feature', description: 'Módulo de Dívidas Tributárias: acompanhe pendências fiscais dos clientes com opção de arquivamento' },
      { type: 'feature', description: 'Página de Notificações: visualize todas as notificações do sistema em um só lugar' },
      { type: 'feature', description: 'Página de histórico de versões: acompanhe as novidades e atualizações do sistema' },
      // Menu e navegação
      { type: 'feature', description: 'Novo design do menu lateral: seções organizadas por área (Gestão, Comercial, Financeiro, etc.) com visual mais limpo' },
      { type: 'feature', description: 'Barra superior integrada ao menu lateral: mais espaço útil na tela e navegação simplificada' },
      // Permissões e usuários
      { type: 'feature', description: 'Redesign das páginas de Usuários e Permissões: interface moderna com cards e agrupamento por perfil' },
      { type: 'feature', description: 'Múltiplas visualizações de usuários: alterne entre lista, tabela e organograma para melhor visualização da equipe' },
      { type: 'feature', description: 'Modal de permissões redesenhado: cards com ícones e descrições para facilitar a configuração de acessos' },
      // Social Selling
      { type: 'feature', description: 'Importação em massa do Instagram: importe múltiplos contatos de uma vez para suas cadências de Social Selling' },
      { type: 'feature', description: 'Landing page de cadências: selecione e gerencie suas cadências de forma visual e intuitiva' },
      // Leads
      { type: 'feature', description: 'Drawer de propostas nos Leads: visualize e gerencie propostas diretamente na ficha do lead' },
      // Melhorias
      { type: 'improvement', description: 'Integração Despesas + Conciliação: despesas pagas aparecem automaticamente como sugestão na conciliação bancária' },
      { type: 'improvement', description: 'Componente de busca aprimorado: busca mais rápida e confiável em todas as listagens do sistema' },
      { type: 'improvement', description: 'Cards de resumo na Prospecção: métricas visuais para acompanhar o funil de prospecção' },
      { type: 'improvement', description: 'Performance do Social Selling: carregamento mais rápido das cadências e resumos' },
      { type: 'improvement', description: 'Layout das configurações padronizado: botões e formulários alinhados em todas as páginas' },
      // Correções
      { type: 'fix', description: 'Redirecionamento incorreto: corrigido bug que redirecionava para página errada ao atualizar o navegador' },
      { type: 'fix', description: 'Solicitações: corrigido erro ao exibir drawer quando usuário do app não está carregado' },
    ],
  },
];

export var CHANGE_TYPES = {
  feature: { label: 'Novo', color: '#52c41a', bgColor: '#f6ffed' },
  improvement: { label: 'Melhoria', color: '#1890ff', bgColor: '#e6f7ff' },
  fix: { label: 'Correção', color: '#faad14', bgColor: '#fffbe6' },
};
